<template>
    <div ref="container" class="vx-tabbed-router relative w-full" :class="position">
        <vx-tabs
            v-bind="$attrs"
            v-on="$listeners"
            :position="position"
            class="router-tabs hide-tab-contents tabs-shadow-none mr-6"
            v-model="activeTabIndex"
            @tab-clicked="tabChanged"
        >
            <vx-tab
                v-for="(tab, index) in localTabs"
                :key="`${tab.route.name}_${index}`"
                :label="tab.label"
                :icon="tab.icon"
                icon-pack="feather"
                :disabled="tab.disabled"
                :class-list="tab.class"
            />
        </vx-tabs>
        <div class="relative w-full route-container pt-4">
            <transition :name="transitionName">
                <router-view v-bind="props" v-on="listeners" />
            </transition>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'vx-tabbed-router',
    props: {
        tabs: {
            type: Array,
            default: () => []
        },
        position: {
            type: String,
            default: 'top'
        },
        props: {
            type: Object,
            default: () => {}
        },
        listeners: {
            type: Object,
            default: () => {}
        }
    },
    watch: {
        activeTabIndex(newV, oldV) {
            if (oldV > newV) {
                this.transitionDirection = 'backward'
            } else {
                this.transitionDirection = 'forward'
            }

            if (this._inactive || newV == null) {
                return
            }
            var tab = this.localTabs[newV] || this.localTabs.first()
            if (this.checkRoutesEqual(tab.destination, this.$route)) {
                return
            }
            this.navigateTo(tab.destination)
        },
        $route: {
            handler: function (newV, oldV) {
                this.activeTabIndex = this.getTabIndexForRoute(newV)
            },
            immediate: true
        }
    },
    data() {
        return {
            transitionDirection: 'forward',
            activeTabIndex: 0
        }
    },
    computed: {
        ...mapGetters({
            getRoute: 'router/getByName'
        }),
        transitionName() {
            if (['left', 'right'].contains(this.position)) {
                return `${this.transitionDirection}-vertical`
            }
            return `${this.transitionDirection}`
        },
        localTabs() {
            return this.tabs
                .map((tab) => {
                    var tabInfoType = typeof tab
                    if (tabInfoType != 'string' && tabInfoType != 'object') {
                        throw 'Tabs must be either the name, tab or route object'
                    }

                    var route = this.getRouteFromTab(tab)
                    var meta = route.meta || {}
                    var destination = this.getDestination(tab)

                    return {
                        icon: tab.icon || meta.icon,
                        label: tab.label || meta.title,
                        disabled: tab.disabled || false,
                        route: route,
                        destination: destination
                    }
                })
                .where((e) => e)
        }
    },
    methods: {
        tabChanged(tabIndex) {
            this.$emit('tab-clicked', this.tabs[tabIndex])
        },
        getQueryString(route) {
            var queries = route.query || {}
            return Object.keys(queries)
                .map((key) => queries[key])
                .join(',')
        },
        getRouteParamString(route) {
            var params = route.params || {}
            return Object.keys(params)
                .map((key) => params[key])
                .join(',')
        },
        checkRoutesEqual(route1, route2) {
            if (!route1 && !route2) {
                return true
            }
            // bitwise xor operator (if either are null but not both, return false)
            if (!!route1 ^ !!route2) {
                return false
            }
            return (
                route1.name === route2.name &&
                this.getQueryString(route1) === this.getQueryString(route2) &&
                this.getRouteParamString(route1) === this.getRouteParamString(route2) // happens when same name but with params(router push to details)
            )
        },
        navigateTo(route) {
            this.$router.replace(route)
        },
        getRouteFromTab(tab) {
            var routeName = this.getRouteNameFromTab(tab)
            return this.getRoute(routeName)
        },
        getRouteNameFromTab(tab) {
            // this is a route name
            if (typeof tab === 'string') {
                return tab
            }

            // this is an actual route from the router
            if (tab.meta) {
                return tab.name
            }

            // this is a custom route object (usually with params)
            if (tab.name) {
                return tab.name
            }

            // this is a custom tab object (usually to manipulate icons, label, disabled, etc)
            if (tab.route) {
                return this.getRouteNameFromTab(tab.route)
            }
            throw 'Invalid tab object'
        },
        getDestination(tab) {
            // this is a route name
            if (typeof tab === 'string') {
                tab = { name: tab }
            }

            // this is an actual route from the router
            if (tab.meta) {
                tab = {
                    name: tab.name
                }
            }

            // this is a custom route object (usually with params)
            if (tab.name) {
                var existingParams = tab.params || {}
                return {
                    ...tab,
                    params: {
                        ...this.$route.params,
                        ...existingParams
                    }
                }
            }

            // this is a custom tab object (usually to manipulate icons, label, disabled, etc)
            if (tab.route) {
                return this.getDestination(tab.route)
            }
            throw 'Invalid tab object'
        },
        getTabIndexForRoute(route) {
            return this.localTabs.findIndex((tab) => this.checkRoutesEqual(tab.destination, route))
        }
    }
}
</script>

<style lang="scss" scoped>
.backward-active,
.forward-active,
.backward-vertical-active,
.forward-vertical-active,
.forward-active {
    transition: all 0.25s ease !important;
}
.forward-enter {
    opacity: 0;
    transform: translateX(25%);
    z-index: 1;
}
.forward-leave-to {
    position: absolute;
    opacity: 0;
    transform: translateX(-25%);
    z-index: 0;
}
.backward-enter {
    opacity: 0;
    transform: translateX(-25%);
    z-index: 0;
}

.backward-leave-to {
    position: absolute;
    opacity: 0;
    transform: translateX(25%);
    z-index: 1;
}

.forward-vertical-enter {
    opacity: 0;
    transform: translateY(25%);
    z-index: 1;
}
.forward-vertical-leave-to {
    position: absolute;
    opacity: 0;
    transform: translateY(-25%);
    z-index: 0;
}
.backward-vertical-enter {
    opacity: 0;
    transform: translateY(-25%);
    z-index: 0;
}
.backward-vertical-leave-to {
    position: absolute;
    opacity: 0;
    transform: translateY(25%);
    z-index: 1;
}

.vx-tabbed-router {
    transition: all 0.25s ease;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    .router-tabs {
        margin-right: 1.5rem;
    }

    .route-container {
        ::v-deep > * {
            width: 100%;
            transition: all 0.3s ease;
        }
    }

    &.left {
        .router-tabs {
            margin-right: 1.5rem;
        }
        flex-direction: row !important;
    }
    &.right {
        flex-direction: row-reverse !important;
    }
}
.hide-tab-contents {
    ::v-deep .con-slot-tabs {
        display: none !important;
    }
}
</style>
